import { ButtonBase, Divider, Stack, Typography } from '@mui/material';
import { getFullLicense } from 'API/license';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { makeStyles } from 'tss-react/mui';
import { CONTEXT_PANEL_WIDTH, NAV_RAIL_WIDTH, NavSection, PanelItem } from './nav-config';

const SECTION_ICON_COLORS: Partial<Record<string, string>> = {
  recruiting: '#FFA502',
  sales: '#FF0A71',
  tests: '#7722FF',
};

interface CreditBarProps {
  label: string;
  used: number;
  total: number;
  color: string;
}

const CreditBar: React.FC<CreditBarProps> = ({ label, used, total, color }) => {
  const { classes } = useStyles();
  const percent = total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;

  return (
    <>
      <Typography className={classes.creditsLabel}>{label}</Typography>
      <div className={classes.creditsTrack}>
        <div className={classes.creditsFill} style={{ width: `${percent}%`, background: color }} />
      </div>
      <Typography className={classes.creditsValue}>
        {used} / {total}
      </Typography>
    </>
  );
};

/** Credits card at the panel bottom (design 2b/2f: credits live here, not in the topbar). */
const ResearchCredits: React.FC<{ showEnrichment: boolean }> = ({ showEnrichment }) => {
  const { classes } = useStyles();
  const [license, setLicense] = useState<any>(null);

  useEffect(() => {
    (async () => {
      const res = await getFullLicense();
      const arr = Array.isArray(res) ? res : [];
      setLicense(arr.find((lic: any) => lic.product === 'saia_recruiting' && lic.isActive && !lic.isExpired) || null);
    })();
  }, []);

  if (!license) return null;

  return (
    <div className={classes.creditsCard}>
      <CreditBar
        label="Research"
        used={license.monthlyResearchCreditsUsed || 0}
        total={license.monthlyResearchCredits || 0}
        color="#4F44FF"
      />
      {showEnrichment ? (
        <CreditBar label="Enrichment" used={license.enrichmentCreditsUsed || 0} total={license.enrichmentCredits || 0} color="#FFA502" />
      ) : null}
    </div>
  );
};

interface PanelRowProps {
  item: PanelItem;
  active: boolean;
  onClick(): void;
}

const PanelRow: React.FC<PanelRowProps> = ({ item, active, onClick }) => {
  const { classes, cx } = useStyles();

  return (
    <ButtonBase className={cx(classes.row, active && classes.rowActive)} onClick={onClick}>
      <span className={classes.rowIcon}>{item.icon}</span>
      <Typography className={classes.rowText}>{item.text}</Typography>
      {item.badge ? <span className={classes.rowBadge}>{item.badge > 99 ? '99+' : item.badge}</span> : null}
    </ButtonBase>
  );
};

interface Props {
  section: NavSection;
  activeItem?: PanelItem;
}

const ContextPanel: React.FC<Props> = ({ section, activeItem }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const mainItems = section.panelItems.filter((item) => !item.settings);
  const settingsItems = section.panelItems.filter((item) => item.settings);

  const isActive = (item: PanelItem) => activeItem?.route === item.route && activeItem?.text === item.text;

  return (
    <aside className={classes.panel} id="pai-context-panel">
      <Stack direction="row" alignItems="center" gap={'10px'}>
        <span className={classes.sectionIcon} style={{ color: SECTION_ICON_COLORS[section.key] ?? '#555' }}>
          {section.icon}
        </span>
        <Typography className={classes.sectionTitle}>{section.label}</Typography>
      </Stack>
      <Stack gap={'2px'}>
        {mainItems.map((item) => (
          <PanelRow key={`${item.route}-${item.text}`} item={item} active={isActive(item)} onClick={() => navigate(item.route)} />
        ))}
      </Stack>
      {settingsItems.length > 0 ? (
        <>
          <Divider className={classes.divider} />
          <Typography className={classes.groupLabel}>{t('settings', 'Einstellungen')}</Typography>
          <Stack gap={'2px'}>
            {settingsItems.map((item) => (
              <PanelRow key={`${item.route}-${item.text}`} item={item} active={isActive(item)} onClick={() => navigate(item.route)} />
            ))}
          </Stack>
        </>
      ) : null}
      <div className={classes.spacer} />
      {section.key === 'recruiting' || section.key === 'research' ? (
        <ResearchCredits showEnrichment={section.key === 'research'} key={location.pathname.split('/')[1]} />
      ) : null}
    </aside>
  );
};

const useStyles = makeStyles()((theme) => ({
  panel: {
    position: 'fixed',
    top: 0,
    left: `${NAV_RAIL_WIDTH}px`,
    bottom: 0,
    width: `${CONTEXT_PANEL_WIDTH}px`,
    boxSizing: 'border-box',
    background: '#FFF',
    borderRight: '1px solid #E3E3E8',
    display: 'flex',
    flexDirection: 'column',
    padding: '14px 14px 12px 14px',
    gap: '12px',
    zIndex: 1150,
    overflowY: 'auto',
  },
  sectionIcon: {
    display: 'flex',
    '& svg': {
      fontSize: theme.typography.pxToRem(20),
    },
  },
  sectionTitle: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 700,
  },
  row: {
    width: '100%',
    height: '34px',
    padding: '0 10px',
    borderRadius: '8px',
    display: 'flex',
    justifyContent: 'flex-start',
    gap: '10px',
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.text.primary,
    '&:hover': {
      background: '#F5F5FB',
    },
  },
  rowActive: {
    background: '#EDECFF',
    color: theme.palette.primary.main,
    '& p': {
      fontWeight: 700,
    },
    '& svg': {
      color: theme.palette.primary.main,
    },
    '&:hover': {
      background: '#EDECFF',
    },
  },
  rowIcon: {
    display: 'flex',
    color: '#555',
    '& svg': {
      fontSize: theme.typography.pxToRem(18),
    },
  },
  rowText: {
    fontSize: theme.typography.pxToRem(13),
    color: 'inherit',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  rowBadge: {
    marginLeft: 'auto',
    minWidth: '20px',
    height: '20px',
    padding: '0 6px',
    boxSizing: 'border-box',
    borderRadius: '999px',
    background: theme.palette.error.main,
    color: '#FFF',
    fontSize: theme.typography.pxToRem(11),
    fontWeight: 700,
    lineHeight: '20px',
    textAlign: 'center',
  },
  divider: {
    background: '#E3E3E8',
  },
  groupLabel: {
    fontSize: theme.typography.pxToRem(10),
    fontWeight: 700,
    letterSpacing: '1.6px',
    textTransform: 'uppercase',
    color: '#707070',
    padding: '0 10px',
  },
  spacer: {
    flex: 1,
  },
  creditsCard: {
    border: '1px solid #E3E3E8',
    borderRadius: '10px',
    padding: '12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  creditsLabel: {
    fontSize: theme.typography.pxToRem(11),
    fontWeight: 700,
    letterSpacing: '1px',
    textTransform: 'uppercase',
    color: '#707070',
  },
  creditsTrack: {
    height: '6px',
    borderRadius: '999px',
    background: '#E3E3E8',
    overflow: 'hidden',
  },
  creditsFill: {
    height: '100%',
  },
  creditsValue: {
    fontSize: theme.typography.pxToRem(12),
    color: '#555',
  },
}));

export default ContextPanel;
